import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiFetch } from '../../utils/apiInterceptor';
import { API_BASE_URL } from '../../utils/paths';
import { deleteSpot, updateSpot } from './savedSpotsSlice';
import { isForecastFresh } from '../../utils/forecastCache';

export const fetchSpotHourlyForecastData = createAsyncThunk(
  'forecast/fetchSpotHourlyForecast',
  async (spotId, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/spots/${spotId}/forecast`, {
        method: 'GET',
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return {
        id: spotId,
        data
      };
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  },
  {
    condition: (spotId, { getState }) => !isForecastFresh(getState().hourlyForecast.spots[spotId]),
  }
);

const hourlyForecastSlice = createSlice({
    name: 'hourlyForecast',
    initialState: {
        spots: {},
        isLoading: false,
        errors: null,
        visibleUnitsHourly: {
          overall_rating_score: true,
          wave_height: true,
          swell_wave_height: true,
          swell_wave_period: true,
          swell_wave_direction: true,
          wind_speed_10m: true,
          wind_speed_80m: false,
          wind_gusts_10m: false,
          wind_direction_10m: true,
          temperature_2m: false,
          rain: false,
          cloud_cover: false,
          sea_level_height_msl: false,
        },
        includedHours: [
          '05:00', '07:00', '09:00', '12:00', '14:00', '17:00', '19:00'
        ] 
    },
    reducers: {
        setIncludedHours: (state, action) => {
            state.includedHours = action.payload;
        },
        setVisibleUnitsHourly: (state, action) => {
          state.visibleUnitsHourly = action.payload;
        },
        removeSpotForecast: (state, action) => {
            delete state.spots[action.payload];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSpotHourlyForecastData.pending, (state) => {
                state.isLoading = true;
                state.errors = null;
            })
            .addCase(fetchSpotHourlyForecastData.fulfilled, (state, action) => {
                state.isLoading = false;
                state.spots[action.payload.id] = action.payload.data;
                state.errors = null;
            })
            .addCase(fetchSpotHourlyForecastData.rejected, (state, action) => {
                state.isLoading = false;
                state.errors = action.payload || { message: 'Failed to fetch forecast' };
            })
            // Listen to spot deletion from savedSpotsSlice
            .addCase(deleteSpot.fulfilled, (state, action) => {
                delete state.spots[action.payload];
            })
            // Preferences/location changed, so the cached ratings are outdated
            .addCase(updateSpot.fulfilled, (state, action) => {
                delete state.spots[action.payload.id];
            })
    }
});

export const { removeSpotForecast } = hourlyForecastSlice.actions;
export default hourlyForecastSlice.reducer